import { Component, Input } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';

export type PreviewDevice = 'mobile' | 'tablette' | 'desktop';

const DEVICE_WIDTHS: Record<PreviewDevice, string> = {
	mobile: '320px',
	tablette: '480px',
	desktop: '100%',
};

/**
 * Aperçu d'un bout de HTML dans un cadre isolé, à la largeur de l'appareil choisi. Le choix de l'appareil
 * appartient à l'écran hôte (barre d'outils de l'éditeur) : ce composant ne fait qu'afficher.
 */
@Component({
    selector: 'dmp-simple-iframe',
    templateUrl: './simple-iframe.component.html',
    styleUrls: ['./simple-iframe.component.scss'],
    standalone: false
})
export class SimpleIframeComponent {

	@Input() displayDevice: PreviewDevice = 'desktop';

	/** Titre du cadre, lu par les lecteurs d'écran. */
	@Input() title = '';

	sanitizedContent: SafeHtml = '';

	constructor(private sanitizer: DomSanitizer) {}

	@Input() set content(content: string) {
		this.updateContent(content);
	}

	get width(): string {
		return DEVICE_WIDTHS[this.displayDevice] ?? DEVICE_WIDTHS.desktop;
	}

	updateContent(content: string) {
		if (!content) {
			this.sanitizedContent = '';
			return;
		}

		// Sanitize HTML to prevent XSS
		const cleanedHtml = this.sanitizeHtml(content);
		this.sanitizedContent = this.sanitizer.bypassSecurityTrustHtml(cleanedHtml);
	}

	private sanitizeHtml(html: string): string {
		// 1. Remove <script> tags
		html = html.replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '');

		// 2. Remove ALL on* event handlers
		html = html.replace(/\son\w+\s*=\s*["'][^"']*["']/gi, '');
		html = html.replace(/\son\w+\s*=\s*[^\s>]*/gi, '');

		// 3. Remove javascript: protocol
		html = html.replace(/javascript:/gi, '');

		// 4. Remove dangerous data: URLs (except images)
		html = html.replace(/src\s*=\s*["']data:(?!image)/gi, 'src="');
		html = html.replace(/href\s*=\s*["']data:/gi, 'href="');

		// 5. Remove srcdoc attribute
		html = html.replace(/srcdoc\s*=\s*["'][^"']*["']/gi, '');

		// 6. Remove dangerous tags
		const dangerousTags = ['iframe', 'object', 'embed', 'applet', 'link', 'base', 'meta', 'form'];
		dangerousTags.forEach(tag => {
			const regex = new RegExp(`<${tag}\\b[^<]*(?:(?!<\/${tag}>)<[^<]*)*<\/${tag}>`, 'gi');
			html = html.replace(regex, '');
			html = html.replace(new RegExp(`<${tag}[^>]*>`, 'gi'), '');
		});

		return html;
	}
}
